import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import DataTable, { type DataTableColumn } from "@/components/base/DataTable";
import EmptyState from "@/components/base/EmptyState";
import ExportMenu from "@/components/base/ExportMenu";
import { Input, Select } from "@/components/base/FormControls";
import StatCard from "@/pages/proprietaire/components/StatCard";
import { useBiens } from "@/pages/proprietaire/biens/BiensProvider";
import { BIEN_TYPE_LABELS } from "@/pages/proprietaire/biens/types";
import { contratReference } from "@/pages/proprietaire/contrats/types";
import { useImmeubles } from "@/pages/proprietaire/immeubles/ImmeublesProvider";
import { useLocataires } from "@/pages/proprietaire/locataires/LocatairesProvider";
import { locataireNomComplet } from "@/pages/proprietaire/locataires/types";
import { monthLabel } from "@/pages/proprietaire/loyers/periods";
import { useLoyers } from "@/pages/proprietaire/loyers/LoyersProvider";
import { LOYER_STATUT_LABELS, LOYER_STATUT_TONES, type Loyer } from "@/pages/proprietaire/loyers/types";
import type { ExportColumn } from "@/utils/export";
import { formatDate, formatFcfa } from "@/utils/format";
import HistoriquePaiementsModal from "./components/HistoriquePaiementsModal";

export default function ImpayesPage() {
  const { impayes, periodes } = useLoyers();
  const { immeubles } = useImmeubles();
  const { biens } = useBiens();
  const { locataires } = useLocataires();
  const [searchParams] = useSearchParams();

  const [search, setSearch] = useState("");
  const [immeubleFilter, setImmeubleFilter] = useState(searchParams.get("immeuble") ?? "");
  const [locataireFilter, setLocataireFilter] = useState(searchParams.get("locataire") ?? "");
  const [periodeFilter, setPeriodeFilter] = useState("");
  const [history, setHistory] = useState<Loyer | null>(null);

  const hasImpayes = impayes.length > 0;

  const immeubleOptions = useMemo(
    () => [{ value: "", label: "Tous les immeubles" }, ...immeubles.map((i) => ({ value: i.id, label: i.nom }))],
    [immeubles]
  );

  const locataireOptions = useMemo(
    () => [
      { value: "", label: "Tous les locataires" },
      ...locataires.map((locataire) => ({ value: locataire.id, label: locataireNomComplet(locataire) })),
    ],
    [locataires]
  );

  const periodeOptions = useMemo(
    () => [{ value: "", label: "Toutes les périodes" }, ...periodes.map((p) => ({ value: p, label: monthLabel(p) }))],
    [periodes]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return impayes.filter((loyer) => {
      const matchesImmeuble = !immeubleFilter || loyer.immeubleId === immeubleFilter;
      const matchesLocataire = !locataireFilter || loyer.locataireId === locataireFilter;
      const matchesPeriode = !periodeFilter || loyer.periode === periodeFilter;

      const bien = biens.find((item) => item.id === loyer.bienId);
      const locataire = locataires.find((item) => item.id === loyer.locataireId);
      const immeuble = immeubles.find((item) => item.id === loyer.immeubleId);
      const matchesSearch =
        !term ||
        [
          monthLabel(loyer.periode),
          bien ? BIEN_TYPE_LABELS[bien.type] : "",
          bien?.reference ?? "",
          locataire ? locataireNomComplet(locataire) : "",
          immeuble?.nom ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(term);

      return matchesImmeuble && matchesLocataire && matchesPeriode && matchesSearch;
    });
  }, [impayes, search, immeubleFilter, locataireFilter, periodeFilter, biens, immeubles, locataires]);

  const isFiltering =
    Boolean(search.trim()) || Boolean(immeubleFilter) || Boolean(locataireFilter) || Boolean(periodeFilter);

  const stats = useMemo(() => {
    const montantDu = filtered.reduce((sum, loyer) => sum + loyer.reste, 0);
    const locatairesConcernes = new Set(filtered.map((loyer) => loyer.locataireId)).size;
    const retardMax = filtered.reduce((max, loyer) => Math.max(max, loyer.joursRetard), 0);
    return { total: filtered.length, montantDu, locatairesConcernes, retardMax };
  }, [filtered]);

  const resetFilters = () => {
    setSearch("");
    setImmeubleFilter("");
    setLocataireFilter("");
    setPeriodeFilter("");
  };

  const exportColumns: ExportColumn<Loyer>[] = [
    { header: "Période", value: (loyer) => monthLabel(loyer.periode) },
    { header: "Immeuble", value: (loyer) => immeubles.find((item) => item.id === loyer.immeubleId)?.nom ?? "" },
    {
      header: "Bien",
      value: (loyer) => {
        const bien = biens.find((item) => item.id === loyer.bienId);
        return bien ? `${BIEN_TYPE_LABELS[bien.type]}${bien.reference ? ` ${bien.reference}` : ""}` : "";
      },
    },
    {
      header: "Locataire",
      value: (loyer) => {
        const locataire = locataires.find((item) => item.id === loyer.locataireId);
        return locataire ? locataireNomComplet(locataire) : "";
      },
    },
    { header: "Montant dû (FCFA)", value: (loyer) => formatFcfa(loyer.reste) },
    { header: "Échéance", value: (loyer) => formatDate(loyer.echeance) },
    { header: "Jours de retard", value: (loyer) => loyer.joursRetard },
    { header: "Statut", value: (loyer) => LOYER_STATUT_LABELS[loyer.statut] },
  ];

  const columns: DataTableColumn<Loyer>[] = [
    {
      key: "periode",
      header: "Période",
      className: "min-w-[120px]",
      render: (loyer) => (
        <span className="font-label font-medium text-foreground-950">{monthLabel(loyer.periode)}</span>
      ),
    },
    {
      key: "locataire",
      header: "Locataire",
      className: "min-w-[170px]",
      render: (loyer) => {
        const locataire = locataires.find((item) => item.id === loyer.locataireId);
        return <span className="text-foreground-700">{locataire ? locataireNomComplet(locataire) : "—"}</span>;
      },
    },
    {
      key: "emplacement",
      header: "Bien · Immeuble",
      className: "min-w-[200px]",
      render: (loyer) => {
        const bien = biens.find((item) => item.id === loyer.bienId);
        const immeuble = immeubles.find((item) => item.id === loyer.immeubleId);
        return (
          <span className="text-foreground-700">
            {bien ? BIEN_TYPE_LABELS[bien.type] : "Non renseigné"}
            {immeuble ? ` · ${immeuble.nom}` : ""}
          </span>
        );
      },
    },
    {
      key: "du",
      header: "Montant dû",
      align: "right",
      className: "min-w-[120px]",
      render: (loyer) => <span className="font-label font-medium text-primary-700">{formatFcfa(loyer.reste)}</span>,
    },
    {
      key: "retard",
      header: "Retard",
      align: "center",
      className: "min-w-[90px]",
      render: (loyer) => <span className="font-label font-medium text-accent-700">{loyer.joursRetard} j</span>,
    },
    {
      key: "echeance",
      header: "Échéance",
      className: "min-w-[120px]",
      render: (loyer) => <span className="text-foreground-700">{formatDate(loyer.echeance)}</span>,
    },
    {
      key: "statut",
      header: "Statut",
      render: (loyer) => (
        <Badge tone={LOYER_STATUT_TONES[loyer.statut]}>{LOYER_STATUT_LABELS[loyer.statut]}</Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      className: "min-w-[300px]",
      render: (loyer) => (
        <div className="flex items-center justify-end gap-2">
          <Link
            to={`/proprietaire/paiements?contrat=${loyer.contratId}&periode=${loyer.periode}&nouveau=1`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md bg-primary-500 px-3.5 py-2 text-xs font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-hand-coin-line text-base" aria-hidden="true" />
            Paiement
          </Link>
          <button
            type="button"
            onClick={() => setHistory(loyer)}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-history-line text-base" aria-hidden="true" />
            Historique
          </button>
          <Link
            to={`/proprietaire/contrats/${loyer.contratId}`}
            className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:border-foreground-300 hover:bg-background-100 cursor-pointer"
          >
            {contratReference(loyer.contratId)}
            <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
          </Link>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="max-w-2xl">
          <h2 className="font-heading text-xl font-semibold text-foreground-950 md:text-2xl">Impayés</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
            Tous les loyers non réglés dont l'échéance est dépassée, avec le montant dû, la durée du retard et l'accès au
            contrat ainsi qu'à l'historique des paiements.
          </p>
        </div>
        <ExportMenu rows={filtered} columns={exportColumns} filename="impayes" title="Impayés" />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard icon="ri-alert-line" label="Impayés" value={String(stats.total)} tone="primary" />
        <StatCard icon="ri-money-cny-circle-line" label="Montant dû" value={formatFcfa(stats.montantDu)} tone="accent" />
        <StatCard icon="ri-user-unfollow-line" label="Locataires concernés" value={String(stats.locatairesConcernes)} tone="secondary" />
        <StatCard
          icon="ri-timer-flash-line"
          label="Retard le plus long"
          value={stats.retardMax > 0 ? `${stats.retardMax} j` : "—"}
          tone="neutral"
        />
      </div>

      {hasImpayes && (
        <section className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5 lg:col-span-2">
              <label htmlFor="imp-search" className="text-xs font-label font-medium text-foreground-700">
                Recherche
              </label>
              <Input
                id="imp-search"
                icon="ri-search-line"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Période, bien, locataire, immeuble..."
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="imp-immeuble" className="text-xs font-label font-medium text-foreground-700">
                Immeuble
              </label>
              <Select
                id="imp-immeuble"
                value={immeubleFilter}
                onChange={(event) => setImmeubleFilter(event.target.value)}
                options={immeubleOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="imp-locataire" className="text-xs font-label font-medium text-foreground-700">
                Locataire
              </label>
              <Select
                id="imp-locataire"
                value={locataireFilter}
                onChange={(event) => setLocataireFilter(event.target.value)}
                options={locataireOptions}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="imp-periode" className="text-xs font-label font-medium text-foreground-700">
                Période
              </label>
              <Select
                id="imp-periode"
                value={periodeFilter}
                onChange={(event) => setPeriodeFilter(event.target.value)}
                options={periodeOptions}
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between gap-3 border-t border-background-200 pt-3">
            <p className="text-xs text-foreground-500">
              {filtered.length} impayé{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""} sur{" "}
              {impayes.length}
            </p>
            {isFiltering && (
              <button
                type="button"
                onClick={resetFilters}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
              >
                <i className="ri-refresh-line text-sm" aria-hidden="true" />
                Réinitialiser
              </button>
            )}
          </div>
        </section>
      )}

      {!hasImpayes && (
        <EmptyState
          icon="ri-checkbox-circle-line"
          title="Aucun impayé"
          description="Bravo, tous vos loyers sont à jour. Les retards apparaîtront ici dès qu'une échéance dépassera sa date sans règlement complet."
          action={
            <Link
              to="/proprietaire/loyers"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-wallet-3-line text-base" aria-hidden="true" />
              Voir les loyers
            </Link>
          }
        />
      )}

      {hasImpayes && filtered.length === 0 && (
        <EmptyState
          icon="ri-search-2-line"
          title="Aucun résultat"
          description="Aucun impayé ne correspond à votre recherche ou aux filtres sélectionnés. Essayez de modifier vos critères."
          action={
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className="ri-refresh-line text-base" aria-hidden="true" />
              Réinitialiser les filtres
            </button>
          }
        />
      )}

      {filtered.length > 0 && (
        <DataTable
          columns={columns}
          rows={filtered}
          rowKey={(loyer) => loyer.id}
          minWidthClass="min-w-[1300px]"
          caption="Impayés"
        />
      )}

      <HistoriquePaiementsModal loyer={history} onClose={() => setHistory(null)} />
    </div>
  );
}